import React, { useRef, useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { getGenreIcons } from '../lib/genre-icons';

interface BookCoverProps {
  title: string;
  author: string;
  genre: string;
  coverImage?: string;
  variant: 'spine' | 'cover';
  progress: number;
  /** seed for deterministic color/style picking */
  seed: string;
  className?: string;
  /** Spine width in px — only used by variant='spine'. Defaults to 44. */
  thickness?: number;
  /** Spine height in px — only used by variant='spine'. Defaults to 224. */
  height?: number;
  /** Year of creation — shown on spine and cover footer */
  year?: number;
  /** Disable the mouse-tilt effect on cover variant (e.g. when inside a 3D flip) */
  disableTilt?: boolean;
}

// Moonlight-token gradient pairs — spine
const SPINE_GRADIENTS = [
  'linear-gradient(180deg, var(--color-primary-800) 0%, var(--color-primary-900) 50%, var(--color-ink-900) 100%)',
  'linear-gradient(180deg, var(--color-ink-800) 0%, var(--color-primary-900) 60%, var(--color-ink-950) 100%)',
  'linear-gradient(180deg, var(--color-accent-800) 0%, var(--color-primary-800) 60%, var(--color-ink-900) 100%)',
  'linear-gradient(180deg, var(--color-primary-700) 0%, var(--color-accent-900) 60%, var(--color-primary-950) 100%)',
  'linear-gradient(180deg, var(--color-ink-700) 0%, var(--color-ink-900) 60%, var(--color-primary-900) 100%)',
  'linear-gradient(180deg, var(--color-accent-900) 0%, var(--color-ink-800) 60%, var(--color-primary-950) 100%)',
];

// Moonlight-token gradient pairs — cover
const COVER_GRADIENTS = [
  'linear-gradient(145deg, var(--color-primary-800) 0%, var(--color-primary-900) 40%, var(--color-ink-900) 100%)',
  'linear-gradient(145deg, var(--color-ink-800) 0%, var(--color-primary-900) 50%, var(--color-ink-950) 100%)',
  'linear-gradient(145deg, var(--color-accent-800) 0%, var(--color-primary-800) 50%, var(--color-ink-900) 100%)',
  'linear-gradient(145deg, var(--color-primary-700) 0%, var(--color-accent-800) 50%, var(--color-ink-900) 100%)',
  'linear-gradient(145deg, var(--color-ink-700) 0%, var(--color-ink-900) 50%, var(--color-primary-900) 100%)',
  'linear-gradient(145deg, var(--color-accent-700) 0%, var(--color-accent-900) 50%, var(--color-ink-950) 100%)',
];

// Cover styles for default covers — deterministic by seed
const COVER_STYLES = ['vintage', 'geometric', 'minimalist', 'bold-title', 'spine-accent'] as const;
type CoverStyle = (typeof COVER_STYLES)[number];

// Fluid title sizing for default (no-image) covers — PLACEHOLDER text only.
// This is used everywhere from ~70px shelf minis up to ~300px lightbox
// covers, so a fixed px/text-* size is always wrong somewhere. Instead we
// size the font as a fraction of the cover's own rendered width (cqw
// container query units — see `containerType` on the cover wrapper below),
// clamped between a hard min/max so it never reads as a full title font on
// a large cover nor overflows a tiny one. Longer titles additionally get a
// smaller cqw fraction so they always have more headroom to clamp/wrap.
function getTitleFontSize(title: string): string {
  const len = title.length;
  if (len > 55) return 'clamp(6px, 5.4cqw, 10px)';
  if (len > 35) return 'clamp(6.5px, 6cqw, 11px)';
  if (len > 18) return 'clamp(7px, 6.6cqw, 12px)';
  return 'clamp(7.5px, 7.2cqw, 13px)';
}

function getTitleClass(coverStyle: CoverStyle): string {
  return coverStyle === 'bold-title'
    ? 'font-bold uppercase tracking-tight'
    : 'font-medium';
}

function hashSeed(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = seed.charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h);
}

export function BookCover({
  title,
  author,
  genre,
  coverImage,
  variant,
  progress,
  seed,
  className = '',
  thickness = 44,
  height = 224,
  year,
  disableTilt = false,
}: BookCoverProps) {
  const h = hashSeed(seed);
  const genreIcons = getGenreIcons(genre);
  const primaryIcon = genreIcons[0];

  // ── Tilt 3D state (cover variant only) ────────────────────────────────────
  const coverRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  // ── Dominant color extracted from the cover image (color-thief) ───────────
  // Used to paint the SPINE in a color that matches the cover, with auto
  // contrast text. Loads the image off-screen and reads its dominant color.
  const [coverColor, setCoverColor] = useState<[number, number, number] | null>(null);
  useEffect(() => {
    if (!coverImage) {
      setCoverColor(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      import('colorthief')
        .then((mod) => {
          if (cancelled) return;
          // colorthief is a CommonJS module (export =); normalize the import shape.
          type ColorThiefCtor = new () => {
            getColor: (img: HTMLImageElement) => [number, number, number];
          };
          const ColorThief = ((mod as unknown as { default?: ColorThiefCtor }).default ??
            (mod as unknown as ColorThiefCtor)) as ColorThiefCtor;
          try {
            const c = new ColorThief().getColor(img);
            if (!cancelled) setCoverColor(c);
          } catch {
            /* canvas/tainted error — keep fallback */
          }
        })
        .catch(() => {});
    };
    img.src = coverImage;
    return () => {
      cancelled = true;
    };
  }, [coverImage]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disableTilt || !coverRef.current) return;
    const box = coverRef.current.getBoundingClientRect();
    const x = e.clientX - box.left - box.width / 2;
    const y = e.clientY - box.top - box.height / 2;
    const factorX = 10 / (box.height / 2);
    const factorY = 10 / (box.width / 2);
    setTilt({ x: -y * factorX, y: x * factorY });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
    setIsHovering(false);
  };

  const handleMouseEnter = () => {
    if (!disableTilt) setIsHovering(true);
  };

  // ── SPINE variant ──────────────────────────────────────────────────────────
  if (variant === 'spine') {
    const spineStyle: React.CSSProperties = { width: thickness };

    // With cover image: paint the spine in the cover's dominant color and keep
    // the book DATA (year + vertical title + genre + status), with auto-contrast text.
    if (coverImage) {
      const [r, g, b] = coverColor ?? [44, 38, 74]; // fallback until extracted
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      const isLight = lum > 0.62;
      const onColor = isLight ? 'rgba(20,16,32,0.92)' : 'rgba(255,255,255,0.92)';
      const onColorDim = isLight ? 'rgba(20,16,32,0.55)' : 'rgba(255,255,255,0.55)';
      const dk = (v: number) => Math.max(0, Math.round(v * 0.55));
      const spineBg = `linear-gradient(180deg, rgb(${r},${g},${b}) 0%, rgb(${dk(r)},${dk(g)},${dk(b)}) 100%)`;
      return (
        <div
          className={`group relative rounded-r-sm cursor-pointer select-none transition-all duration-300 ease-out ${className}`}
          style={{
            ...spineStyle,
            height,
            background: spineBg,
            boxShadow:
              'inset 1px 0 0 rgba(255,255,255,0.12), inset -3px 0 6px rgba(0,0,0,0.45), 3px 4px 12px rgba(0,0,0,0.35)',
            borderLeft: '2px solid rgba(0,0,0,0.2)',
          }}
        >
          {/* Shadow overlay next to book */}
          <div className="absolute inset-y-0 -left-1 w-1 bg-black/30 blur-[1px] group-hover:bg-black/10 transition-colors pointer-events-none" />
          {/* 3D edge lines */}
          <div className="absolute inset-y-0 left-0 w-[3px] bg-white/15 pointer-events-none" />
          <div className="absolute inset-y-0 left-[3px] w-[1px] bg-black/20 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-[3px] bg-black/40 pointer-events-none" />
          {/* Binding lines (contrast-aware) */}
          <div className="absolute inset-x-0 top-3 h-[1px] pointer-events-none" style={{ background: onColorDim, opacity: 0.5 }} />
          <div className="absolute inset-x-0 bottom-3 h-[1px] pointer-events-none" style={{ background: onColorDim, opacity: 0.5 }} />

          {/* Content — year / vertical title / genre + status */}
          <div className="relative z-10 flex flex-col items-center justify-between h-full py-3 px-0.5">
            <div className="text-[7px] font-mono tracking-widest uppercase leading-none" style={{ color: onColorDim }}>
              {year ?? ''}
            </div>
            <div className="flex-1 flex items-center justify-center overflow-hidden py-2">
              <span
                className="text-[9px] md:text-[10px] font-serif font-semibold tracking-wide uppercase whitespace-nowrap overflow-hidden text-ellipsis inline-block"
                style={{
                  writingMode: 'vertical-rl',
                  color: onColor,
                  textShadow: isLight ? 'none' : '0 1px 2px rgba(0,0,0,0.5)',
                  maxHeight: Math.round(height * 0.58),
                }}
              >
                {title}
              </span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Icon icon={primaryIcon} width={10} height={10} style={{ color: onColorDim }} />
              {progress >= 100 ? (
                <div className="w-1.5 h-1.5 rounded-full bg-accent/80 border border-white/20" />
              ) : progress > 0 ? (
                <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: onColorDim }} />
              ) : (
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: onColorDim, opacity: 0.4 }} />
              )}
            </div>
          </div>
        </div>
      );
    }

    // Default rich spine — gradient, year, vertical title, genre icon
    const spineGrad = SPINE_GRADIENTS[h % SPINE_GRADIENTS.length];
    return (
      <div
        className={`group relative rounded-r-sm cursor-pointer select-none transition-all duration-300 ease-out ${className}`}
        style={{
          ...spineStyle,
          height,
          background: spineGrad,
          boxShadow:
            'inset 1px 0 0 rgba(255,255,255,0.12), inset -3px 0 6px rgba(0,0,0,0.45), 3px 4px 12px rgba(0,0,0,0.35)',
          borderLeft: '2px solid rgba(0,0,0,0.2)',
        }}
      >
        {/* Shadow overlay next to book */}
        <div className="absolute inset-y-0 -left-1 w-1 bg-black/30 blur-[1px] group-hover:bg-black/10 transition-colors pointer-events-none" />

        {/* 3D edge lines — like mock */}
        <div className="absolute inset-y-0 left-0 w-[3px] bg-white/15 pointer-events-none" />
        <div className="absolute inset-y-0 left-[3px] w-[1px] bg-black/20 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-[3px] bg-black/40 pointer-events-none" />

        {/* Vintage binding lines */}
        <div className="absolute inset-x-0 top-3 h-[1px] bg-white/20 pointer-events-none" />
        <div className="absolute inset-x-0 bottom-3 h-[1px] bg-white/20 pointer-events-none" />

        {/* Content — flex column top-to-bottom */}
        <div className="relative z-10 flex flex-col items-center justify-between h-full py-3 px-0.5">
          {/* TOP: Year */}
          <div className="text-[7px] font-mono tracking-widest uppercase text-white/70 leading-none">
            {year ?? ''}
          </div>

          {/* CENTER: Vertical title */}
          <div className="flex-1 flex items-center justify-center overflow-hidden py-2">
            <span
              className="text-[9px] md:text-[10px] font-serif font-semibold tracking-wide uppercase text-white/90 whitespace-nowrap overflow-hidden text-ellipsis inline-block"
              style={{
                writingMode: 'vertical-rl',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                maxHeight: Math.round(height * 0.58),
              }}
            >
              {title}
            </span>
          </div>

          {/* BOTTOM: Genre icon + dot */}
          <div className="flex flex-col items-center gap-1">
            <Icon
              icon={primaryIcon}
              className="text-white/50"
              width={10}
              height={10}
            />
            {/* Progress/status dot */}
            {progress >= 100 ? (
              <div className="w-1.5 h-1.5 rounded-full bg-accent/80 border border-white/20" />
            ) : progress > 0 ? (
              <div className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse" />
            ) : (
              <div className="w-1.5 h-1.5 rounded-full bg-white/15" />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── COVER variant ──────────────────────────────────────────────────────────
  const tiltTransform = disableTilt
    ? undefined
    : `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${isHovering ? 1.03 : 1})`;
  const tiltShadow = (disableTilt || !isHovering)
    ? 'inset 5px 0 12px rgba(255,255,255,0.06), inset -3px 0 6px rgba(0,0,0,0.3), 0 20px 40px -8px rgba(0,0,0,0.55)'
    : '0 28px 60px -10px rgba(0,0,0,0.7), inset 5px 0 12px rgba(255,255,255,0.06), inset -3px 0 6px rgba(0,0,0,0.3)';

  // With cover image: tilt + shine, no text/badges/overlay
  if (coverImage) {
    return (
      <div
        ref={coverRef}
        className={`group relative w-full overflow-hidden rounded-r-md select-none ${className}`}
        style={{
          aspectRatio: '2/3',
          boxShadow: tiltShadow,
          transform: tiltTransform,
          // preserve-3d breaks overflow:hidden clipping in Chromium/Edge when
          // there's no actual 3D transform — only enable it while tilting.
          transformStyle: disableTilt ? undefined : 'preserve-3d',
          transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
      >
        <img src={coverImage} alt={title} className="w-full h-full object-cover" />
        {/* Spine edge structural only — no text/icons */}
        <div className="absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-black/25 to-transparent pointer-events-none" />
        {/* Shine sweep on hover */}
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-[900ms] pointer-events-none" />
      </div>
    );
  }

  // Default rich cover — no image
  const coverGrad = COVER_GRADIENTS[h % COVER_GRADIENTS.length];
  const coverStyle: CoverStyle = COVER_STYLES[h % COVER_STYLES.length];

  return (
    <div
      ref={coverRef}
      className={`group relative overflow-hidden rounded-r-md select-none flex flex-col justify-between p-4 ${className}`}
      style={{
        aspectRatio: '2/3',
        background: coverGrad,
        boxShadow: tiltShadow,
        transform: tiltTransform,
        // preserve-3d breaks overflow:hidden clipping in Chromium/Edge when
        // there's no actual 3D transform — only enable it while tilting.
        transformStyle: disableTilt ? undefined : 'preserve-3d',
        transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
        // container query context so the placeholder title (cqw units) scales
        // with the ACTUAL rendered cover size (77px shelf mini → 300px lightbox)
        // instead of a fixed px/text-* size that overflows small covers.
        containerType: 'inline-size',
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      {/* ── Cover style overlays ─────────────────────────────────────────── */}

      {/* vintage — double inner border */}
      {coverStyle === 'vintage' && (
        <>
          <div className="absolute inset-2 border border-white/25 pointer-events-none rounded" />
          <div className="absolute inset-3 border border-white/10 pointer-events-none rounded" />
          <div className="absolute inset-x-0 top-3 h-[1px] bg-yellow-400/20 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-3 h-[1px] bg-yellow-400/20 pointer-events-none" />
        </>
      )}

      {/* geometric — diagonal gradient + orb */}
      {coverStyle === 'geometric' && (
        <>
          <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-white/10 mix-blend-overlay pointer-events-none" />
          <div className="absolute -top-1/4 -right-1/4 w-40 h-40 rounded-full bg-white/5 blur-xl pointer-events-none" />
        </>
      )}

      {/* minimalist — corner accent */}
      {coverStyle === 'minimalist' && (
        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-white/10 to-transparent pointer-events-none" />
      )}

      {/* bold-title — diagonal slash */}
      {coverStyle === 'bold-title' && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/50 pointer-events-none" />
      )}

      {/* spine-accent — cloth-bound left edge */}
      {coverStyle === 'spine-accent' && (
        <div className="absolute left-0 inset-y-0 w-5 bg-black/30 border-r border-white/10 pointer-events-none" />
      )}

      {/* Moon glow orb */}
      <div
        className="absolute right-4 top-8 w-14 h-14 rounded-full opacity-15 blur-xl pointer-events-none"
        style={{ background: 'var(--color-accent)' }}
      />

      {/* Spine hinge shadow (left edge) */}
      <div className="absolute left-0 inset-y-0 w-3 bg-gradient-to-r from-black/35 to-transparent pointer-events-none" />
      <div className="absolute left-[8px] inset-y-0 w-[1px] bg-white/15 pointer-events-none" />
      <div className="absolute left-0 inset-y-0 w-[3px] bg-black/25 pointer-events-none" />

      {/* Shine sweep on hover */}
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/12 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-[900ms] pointer-events-none" />

      {/* ── Content ──────────────────────────────────────────────────────── */}

      {/* Header — Author */}
      <div className="relative z-10 text-center">
        {author && (
          <p className="text-[9px] uppercase tracking-[0.2em] font-medium text-white/70 truncate leading-relaxed font-mono">
            {author}
          </p>
        )}
        <div className="w-8 h-[1px] bg-white/30 mx-auto mt-1.5" />
      </div>

      {/* Center — Primary genre icon (large) + title + secondary icons */}
      <div className="relative z-10 flex flex-col items-center text-center px-1 py-2 flex-1 min-h-0 justify-center overflow-hidden">
        {/* Genre symbol(s) — one per defined genre, small, side by side. No circle. */}
        <div className="mb-3 flex items-center justify-center gap-2 shrink-0">
          {genreIcons.map((icon) => (
            <Icon key={icon} icon={icon} className="text-white/75" width={18} height={18} />
          ))}
        </div>

        {/* Title — PLACEHOLDER text, sized as a fraction of the cover's own
            rendered width (cqw, see containerType on the wrapper) so it's
            always proportionate, from ~70px shelf minis to ~300px lightbox
            covers. leading-tight + line-clamp-3 + a matching maxHeight cap
            (belt-and-suspenders, in case -webkit-line-clamp isn't fully
            honored by a flex ancestor's overflow:hidden in some engines)
            guarantee it can NEVER overflow/burst the cover frame. */}
        <h3
          className={`font-serif text-white leading-tight line-clamp-3 w-full max-w-full overflow-hidden shrink-0 ${getTitleClass(coverStyle)}`}
          style={{
            fontSize: getTitleFontSize(title),
            textShadow: '0 2px 6px rgba(0,0,0,0.5)',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            hyphens: 'auto',
            maxHeight: '3.6em',
          }}
        >
          {title}
        </h3>

        <div className="h-px w-8 bg-white/30 my-2 rounded-full" />

        {author && (
          <p className="text-white/50 text-[8px] font-mono tracking-wide italic">{author}</p>
        )}
      </div>

      {/* Footer — genre · year, CENTERED like the rest of the cover (era
          justify-between → o gênero/ano ficavam colados à esquerda, "quebrando"
          a simetria da capa). Progresso vira um selo discreto abaixo. */}
      <div className="relative z-10 flex flex-col items-center gap-0.5 text-center min-w-0">
        {genre && (
          <span className="max-w-full text-[7px] font-mono tracking-widest uppercase text-white/50 truncate">
            {genre}
          </span>
        )}
        {year && (
          <span className="text-[7px] font-mono text-white/40 leading-none">{year}</span>
        )}
        {progress > 0 && (
          <span className="mt-0.5 text-[7.5px] font-mono px-1.5 py-0.5 rounded-full bg-black/40 border border-white/10 text-white/70">
            {progress >= 100 ? 'Concluído' : `${progress}%`}
          </span>
        )}
      </div>
    </div>
  );
}
